import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Typography, CircularProgress, Alert, Button, Box } from "@mui/material";
import { Inventory2, TaskAltOutlined } from "@mui/icons-material";
import { useAuth } from "../../context/authContext";
import { useNotifications } from "../../context/NotificationContext";
import { useTranslation } from "react-i18next";

import PageTitle from "../../components/PageTitle/PageTitle";
import OrderDetails from "../../components/OrderDetails/OrderDetails";
import { ALERT_MESSAGES, BUTTON, TITLES, UI_TEXT } from "../../constants";
import { getOrderByKey } from "../../services/breadCrumbsService";
import "./OrderConfirmation.css";

const OrderConfirmation = () => {
    const { orderKey } = useParams();
    const { user } = useAuth();
    const { refreshNotifications } = useNotifications();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [orderData, setOrderData] = useState(null);
    const navigate = useNavigate();
    const { t } = useTranslation();

    useEffect(() => {
        refreshNotifications(500, 0);
    }, [refreshNotifications]);

    useEffect(() => {
        const fetchOrder = async () => {
            try {
                const req_body = { orderKey };
                const response = await getOrderByKey(req_body);

                if (response.data && response.status) {
                    const orderDetails = response.data;
                    setOrderData(orderDetails);
                } else {
                    setError(response.data.message || t("ALERT_MESSAGES.ERROR_OCCURED_FETCHING_ORDER"));
                }
            } catch (error) {
                console.error("Error while fetching the order", error);
                setError(t("ALERT_MESSAGES.ERROR_OCCURED_FETCHING_PLEASE_TRY_AGAIN"));
            } finally {
                setLoading(false);
            }
        };
        fetchOrder();
    }, [orderKey, t]);

    if (loading)
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "80vh",
                }}
            >
                <CircularProgress size={60} />
            </Box>
        );

    if (error)
        return (
            <Alert severity="error" sx={{ m: 3 }}>
                {error}
            </Alert>
        );

    if (!orderData) return null;

    const { order } = orderData;

    const handleViewAllOrders = () => {
        navigate("/order-history");
    };

    return (
        <Box
            sx={{
                width: "80vw",
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-start",
                alignItems: "flex-start",
                minHeight: "800px",
                padding: "40px",
            }}
        >
            <PageTitle title={t("TITLES.ORDER_CONFIRMATION")} />

            <Box
                sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    backgroundColor: (theme) => theme.palette.success.light + "20",
                    borderRadius: "10px",
                    py: 2,
                    px: 2.5,
                    columnGap: 2,
                    width: '100%'
                }}
            >
                <TaskAltOutlined sx={{ color: "success.main", fontSize: "50px" }} />
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: "bold", color: 'success.main' }}>
                        Thank You, and add ! <b>{t("UI_TEXT.ORDERS")}</b> {order.order_number}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Thank you for creating the quote, {user.user_first_name}
                    </Typography>
                </Box>
            </Box>

            <Typography variant="body1" sx={{ px: 1, mt: 1 }}>
                {t("UI_TEXT.CONFIRMATION_EMAIL_SENT")}
            </Typography>
            <Box sx={{ width: '100%' }}>
                <OrderDetails orderData={orderData} />
            </Box>

            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", mt: 1, width: '100%' }}>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<Inventory2 />}
                    onClick={handleViewAllOrders}
                >
                    {t("BUTTON.VIEW_ALL_ORDERS")}
                </Button>
            </Box>
        </Box>
    );
};

export default OrderConfirmation;
